/**
 * Row history: the runs one table row started, across all automations of its
 * table the user may view, newest first and paged by cursor. Opened from the
 * row menu of the table views ("Automationsläufe").
 */
import { Button, Loading } from '@affine/component';
import { workspaceApiFetch } from '@affine/core/modules/cloud';
import { useI18n } from '@affine/i18n';
import { streamLainAnimationController } from '@blocksuite/affine-shared/utils';
import { ArrowLeftSmallIcon } from '@blocksuite/icons/rc';
import { useService } from '@toeverything/infra';
import clsx from 'clsx';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';

import { WorkspaceService } from '../../../workspace';
import type { RowHistoryTarget } from '../../services/automation';
import { type ExecutionLog, LoggingService } from '../../services/logging';
import { workflowWorkspaceRoutes } from '../../services/workflow-workspace-routes';
import * as panelStyles from '../automation-panel.css';
import { LogEntry } from './history-panel';
import * as styles from './history-panel.css';
import {
  historyForegroundPolicy,
  useHistoryRunActions,
} from './history-run-actions';

type LoadState = 'loading' | 'ready' | 'error';

export const RowHistoryPanel: FC<{
  target: RowHistoryTarget;
  onClose: () => void;
}> = ({ target, onClose }) => {
  const t = useI18n();
  const loggingService = useService(LoggingService);
  const workspaceId = useService(WorkspaceService).workspace.id;
  const { databaseId, rowId } = target;
  const [logs, setLogs] = useState<ExecutionLog[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [state, setState] = useState<LoadState>('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());
  // Only the newest request may write the list (row switch, refresh).
  const generation = useRef(0);

  const loadFirstPage = useCallback(async (): Promise<void> => {
    const run = ++generation.current;
    setState('loading');
    try {
      const page = await loggingService.fetchRowHistory(workspaceId, {
        databaseId,
        rowId,
        cursor: null,
      });
      if (run !== generation.current) return;
      setLogs(page.logs);
      setNextCursor(page.nextCursor);
      setState('ready');
    } catch (error) {
      if (run !== generation.current) return;
      console.error('[RowHistoryPanel] Failed to load the row history:', error);
      setState('error');
    }
  }, [databaseId, loggingService, rowId, workspaceId]);

  useEffect(() => {
    setExpandedLogId(null);
    void loadFirstPage();
  }, [loadFirstPage]);

  const loadOlder = useCallback(async (): Promise<void> => {
    if (!nextCursor || loadingMore) return;
    const run = generation.current;
    setLoadingMore(true);
    try {
      const page = await loggingService.fetchRowHistory(workspaceId, {
        databaseId,
        rowId,
        cursor: nextCursor,
      });
      if (run !== generation.current) return;
      const firstNewId = page.logs[0]?.id;
      setLogs(current => {
        const known = new Set(current.map(log => log.id));
        return [...current, ...page.logs.filter(log => !known.has(log.id))];
      });
      setNextCursor(page.nextCursor);
      if (firstNewId) {
        requestAnimationFrame(() => {
          const list = listRef.current;
          const row = rowRefs.current.get(firstNewId);
          if (!list || !row) return;
          streamLainAnimationController.scrollTo(list, {
            top: Math.max(0, row.offsetTop - list.offsetTop),
          });
        });
      }
    } catch (error) {
      console.error('[RowHistoryPanel] Failed to load older runs:', error);
      setState('error');
    } finally {
      setLoadingMore(false);
    }
  }, [databaseId, loadingMore, loggingService, nextCursor, rowId, workspaceId]);

  const { cancelOp, cancelExecution, retryOp, downloadOp } =
    useHistoryRunActions(workspaceId, loadFirstPage);

  const handleDelete = useCallback(
    (logId: string): void => {
      setLogs(current => current.filter(log => log.id !== logId));
      if (expandedLogId === logId) setExpandedLogId(null);
      workspaceApiFetch(
        workflowWorkspaceRoutes.log(workspaceId, logId),
        { method: 'DELETE' },
        historyForegroundPolicy(workspaceId, `delete-log:${logId}`)
      )
        .then(response => {
          if (!response.ok) {
            console.error(
              '[RowHistoryPanel] Backend delete failed:',
              response.status
            );
            return loadFirstPage();
          }
          return undefined;
        })
        .catch(error => {
          console.error('[RowHistoryPanel] Failed to delete log:', error);
          void loadFirstPage();
        });
    },
    [expandedLogId, loadFirstPage, workspaceId]
  );

  return (
    <div
      className={clsx(panelStyles.section, styles.panel)}
      data-testid="automation-row-history"
    >
      <div className={styles.rowHistoryHeader}>
        <button
          type="button"
          className={styles.iconButton}
          onClick={onClose}
          aria-label={t['com.streamlain.automation.row-history.back']()}
          title={t['com.streamlain.automation.row-history.back']()}
        >
          <ArrowLeftSmallIcon width={20} height={20} />
        </button>
        <span className={styles.rowHistoryTitle}>
          {t['com.streamlain.automation.row-history.title']()}
        </span>
      </div>

      {state === 'loading' ? (
        <div className={styles.empty} role="status">
          <Loading size={16} />
        </div>
      ) : state === 'error' ? (
        <div className={styles.rowHistoryError} role="alert">
          <span className={styles.empty}>
            {t['com.streamlain.automation.row-history.load-failed']()}
          </span>
          <Button variant="secondary" onClick={() => void loadFirstPage()}>
            {t['com.streamlain.automation.row-history.retry']()}
          </Button>
        </div>
      ) : logs.length === 0 ? (
        <div className={styles.empty}>
          {t['com.streamlain.automation.row-history.empty']()}
        </div>
      ) : (
        <div ref={listRef} className={styles.list}>
          {logs.map(log => (
            <LogEntry
              key={log.id}
              log={log}
              isExpanded={expandedLogId === log.id}
              onToggle={() =>
                setExpandedLogId(current =>
                  current === log.id ? null : log.id
                )
              }
              onDelete={() => handleDelete(log.id)}
              onCancelOp={cancelOp}
              onCancelExecution={cancelExecution}
              onRetryOp={retryOp}
              onDownloadOp={downloadOp}
              rowRef={el => {
                if (el) rowRefs.current.set(log.id, el);
                else rowRefs.current.delete(log.id);
              }}
            />
          ))}
        </div>
      )}

      {state === 'ready' && nextCursor && (
        <Button
          variant="secondary"
          className={styles.rowHistoryMore}
          loading={loadingMore}
          onClick={() => void loadOlder()}
        >
          {t['com.streamlain.automation.row-history.more']()}
        </Button>
      )}
    </div>
  );
};
